import { globalEmitter, emitterProps } from '@/components/helpers/emitter-event'
import type { SwymItemPayload, SwymList, SwymListItem } from './swym.types'

/**
 * Thin wrapper over Swym Wishlist Plus (`window._swat`).
 * Owns a cached copy of the default list and broadcasts changes on the
 * `wishlist.change` emitter event. All failures are swallowed — wishlist
 * must never break the storefront.
 */
class SwymService {
  private swatPromise: Promise<any> | null = null
  private lid: string | null = null
  private lidPromise: Promise<string> | null = null
  private items: SwymListItem[] = []
  private loaded = false

  ready(): Promise<any> {
    // Swym's SwymCallbacks queue only drains while its script is loading — once
    // window._swat exists it stops watching the array, so a callback queued after
    // that point (e.g. our idle-deferred init) would never resolve.
    if (window._swat) return Promise.resolve(window._swat)
    if (!this.swatPromise) {
      this.swatPromise = new Promise((resolve) => {
        window.SwymCallbacks = window.SwymCallbacks || []
        window.SwymCallbacks.push((swat: any) => resolve(swat))
      })
    }
    return this.swatPromise
  }

  private async getListId(swat: any): Promise<string> {
    if (this.lid) return this.lid
    if (!this.lidPromise) {
      this.lidPromise = (async () => {
        const lists: SwymList[] = await new Promise((resolve, reject) => {
          swat.fetchLists({ callbackFn: resolve, errorFn: reject })
        })
        let lid: string
        if (lists.length > 0) {
          lid = lists[0].lid
        } else {
          const list: SwymList = await new Promise((resolve, reject) => {
            swat.createList({ lname: 'My wishlist' }, resolve, reject)
          })
          lid = list.lid
        }
        this.lid = lid
        return lid
      })().catch((error) => {
        this.lidPromise = null
        throw error
      })
    }
    return this.lidPromise
  }

  async getList(): Promise<SwymListItem[]> {
    try {
      const swat = await this.ready()
      const lid = await this.getListId(swat)
      const list: SwymList = await new Promise((resolve, reject) => {
        swat.fetchListDetails({ lid }, resolve, reject)
      })
      // The Swym JS API (_swat) actually returns `items`, while the REST docs
      // describe `listcontents` — cover both so getList() works either way.
      this.items = list.listcontents ?? list.items ?? []
      this.loaded = true
      this.emitChange()
      return this.items
    } catch (error) {
      console.warn('[swym] getList failed', error)
      return this.items
    }
  }

  has(empi: number): boolean {
    return this.items.some((item) => Number(item.empi) === Number(empi))
  }

  hasVariant(epi: number): boolean {
    return this.items.some((item) => Number(item.epi) === Number(epi))
  }

  /**
   * Read-only: the cached entries for a product. Deliberately dumb — whether
   * an entry counts as "size-selected" is a `cprops` question, and that's
   * interpreted in exactly one place (`swym-entry.ts`'s `isSizeSelected`),
   * not here.
   */
  entriesFor(empi: number): SwymListItem[] {
    return this.items.filter((item) => Number(item.empi) === Number(empi))
  }

  /** Swym's own item key is (empi, epi) — a product can hold one entry per size. */
  private isSameEntry(item: SwymListItem | SwymItemPayload, other: SwymItemPayload): boolean {
    return Number(item.empi) === Number(other.empi) && Number(item.epi) === Number(other.epi)
  }

  count(): number {
    return this.items.length
  }

  isLoaded(): boolean {
    return this.loaded
  }

  async add(item: SwymItemPayload): Promise<boolean> {
    try {
      const swat = await this.ready()
      const lid = await this.getListId(swat)
      await new Promise((resolve, reject) => {
        swat.addToList(lid, item, resolve, reject)
      })
      if (!this.loaded) {
        await this.getList()
      } else if (!this.hasVariant(item.epi)) {
        this.items = [...this.items, item]
        this.emitChange()
      } else {
        // The server call above just succeeded for an (empi, epi) already in
        // the cache — e.g. a size-explicit save of the exact variant a PLP
        // heart had defaulted to. That cached entry may still carry no
        // `cprops`, so adopt whatever this payload sent onto it (a new array,
        // never mutated in place — every other mutation in this class
        // reassigns `this.items` and consumers rely on that) rather than
        // leaving it reading as product-level until a reload. `item.cprops ??
        // cached.cprops` rather than `item.cprops` alone: a `cprops`-less
        // payload here is unreachable today (the only other `add()` caller
        // runs when the product isn't already saved), but if that ever
        // changed, assigning unconditionally would silently downgrade an
        // already size-selected cached entry to product-level.
        this.items = this.items.map((cached) =>
          this.isSameEntry(cached, item) ? { ...cached, cprops: item.cprops ?? cached.cprops } : cached
        )
        this.emitChange()
      }
      return true
    } catch (error) {
      console.warn('[swym] add failed', error)
      return false
    }
  }

  /**
   * Deletes exactly one entry, matched on (empi, epi). Posting a `du` that
   * differs from the one the server stored returns a 400, so the stored value is
   * borrowed whenever the entry is in the cached list.
   */
  async removeVariant(item: SwymItemPayload): Promise<boolean> {
    try {
      const swat = await this.ready()
      const lid = await this.getListId(swat)
      const stored = this.items.find((i) => this.isSameEntry(i, item))
      const payload = {
        epi: Number(item.epi),
        empi: Number(item.empi),
        du: stored?.du ?? item.du
      }
      await new Promise((resolve, reject) => {
        swat.deleteFromList(lid, payload, resolve, reject)
      })
      if (!this.loaded) {
        await this.getList()
      } else {
        this.items = this.items.filter((i) => !this.isSameEntry(i, item))
        this.emitChange()
      }
      return true
    } catch (error) {
      console.warn('[swym] removeVariant failed', error)
      return false
    }
  }

  /**
   * Deletes every entry for a product. Used by surfaces with no size context:
   * the PLP/search/recs card hearts, and the PDP heart when no size is selected.
   * Swym's JS API has no bulk delete, so entries go one at a time.
   */
  async removeProduct(empi: number): Promise<boolean> {
    // Deletes that succeed before a later one rejects must not be left
    // counted in the cache (and therefore the header badge and grid) just
    // because the loop below never reached its post-loop filter — tracked
    // here and filtered/emitted in `finally` regardless of how the try exits.
    const succeeded: SwymListItem[] = []
    try {
      const swat = await this.ready()
      const lid = await this.getListId(swat)
      if (!this.loaded) await this.getList()
      const entries = this.items.filter((item) => Number(item.empi) === Number(empi))
      if (!entries.length) return true
      for (const entry of entries) {
        await new Promise((resolve, reject) => {
          swat.deleteFromList(
            lid,
            { epi: Number(entry.epi), empi: Number(entry.empi), du: entry.du },
            resolve,
            reject
          )
        })
        succeeded.push(entry)
      }
      return true
    } catch (error) {
      console.warn('[swym] removeProduct failed', error)
      return false
    } finally {
      if (succeeded.length) {
        this.items = this.items.filter((item) => !succeeded.some((s) => this.isSameEntry(item, s)))
        this.emitChange()
      }
    }
  }

  /**
   * Replaces one entry with another. Swym has no update-list-item call, so a
   * changed size or a swatch-triggered product swap has to be a delete of the
   * old (empi, epi) plus an add of the new one — and the two are ordered
   * add-then-delete, not delete-then-add: if `add` fails, `oldItem` is
   * untouched and the shopper's list is exactly as it was; if `removeVariant`
   * fails after a successful `add`, the shopper is left with both entries
   * rather than neither — a visible duplicate to tidy up later, never a
   * silent loss of what they just saved. That asymmetry is why this returns
   * `true` once `add` succeeds regardless of whether the cleanup delete does.
   *
   * `newItem`'s (empi, epi) may already be a cached entry — the shopper
   * picked a size another of their own cards already holds. `add()` upserts
   * onto that cached entry rather than duplicating it (see its own comment),
   * so the two collapse into one as soon as `oldItem` is removed below; no
   * special-casing needed here.
   */
  async replaceEntry(oldItem: SwymItemPayload, newItem: SwymItemPayload): Promise<boolean> {
    if (this.isSameEntry(oldItem, newItem)) return true // nothing changed

    const added = await this.add(newItem)
    if (!added) return false

    const removed = await this.removeVariant(oldItem)
    if (!removed) {
      console.warn('[swym] replaceEntry: added the new entry but failed to remove the old one; both remain saved')
    }
    return true
  }

  async shareUrl(): Promise<string | null> {
    try {
      const swat = await this.ready()
      const lid = await this.getListId(swat)
      return await new Promise((resolve, reject) => {
        swat.generateSharedListURL(lid, resolve, reject)
      })
    } catch (error) {
      console.warn('[swym] shareUrl failed', error)
      return null
    }
  }

  // Renders Swym's own read-only shared-list widget into `container` (the
  // /pages/swym-share-wishlist landing page shareUrl() links to). Swym reads
  // the list id straight off the URL query string itself — nothing here
  // touches getListId()/this.lid, which track the current VISITOR's list.
  async renderSharedWishlist(container: HTMLElement, strings: Record<string, string> = {}): Promise<void> {
    try {
      const swat = await this.ready()
      swat.ui.settings.Strings.ShareWishlistPopupHeading = strings.share_wishlist_popup_heading
      swat.ui.settings.Strings.ShareWishlistNamePlaceholder = strings.share_wishlist_name_placeholder
      swat.ui.settings.Strings.ShareWishlistEmailPlaceholder = strings.share_wishlist_email_placeholder
      swat.ui.settings.Strings.ShareWishlistNotePlaceholder = strings.share_wishlist_note_placeholder
      swat.ui.settings.Strings.ShareWishlistEmailCTA = strings.share_wishlist_cta
      const queryParams = swat.utils.getEncodedAsObject(window.location.search)
      swat.ui.renderSharedWishlistInContainer(container, queryParams)
    } catch (error) {
      console.warn('[swym] renderSharedWishlist failed', error)
    }
  }

  private emitChange(): void {
    globalEmitter.emit(emitterProps.wishlist.change, {
      count: this.items.length,
      empis: this.items.map((item) => Number(item.empi)),
      items: this.items
    })
  }
}

export const swymService = new SwymService()
